import { httpRequest } from '@/shared/lib/http'

/**
 * Paquete mensual de documentos (C-07). Las obligaciones patronales que un
 * proveedor de Servicios entrega cada mes, distintas del expediente de alta.
 *
 * El contrato lo definió SP-220 y está en inglés, incluidas las llaves del JSON.
 * Aquí no se traduce: renombrar de este lado obliga a mapear en los dos
 * sentidos y a mantener dos vocabularios para lo mismo.
 *
 * El token va explícito en cada llamada: el mismo componente sirve para el
 * portal del proveedor y para el panel interno, así que deducirlo de la ruta
 * mandaría el equivocado.
 */

export type EstadoPaquete = 'pending' | 'in_review' | 'approved' | 'rejected'
export type EstadoDocumento = 'pending' | 'approved' | 'rejected'

export interface DocumentoDelPaquete {
  id: string
  monthlyPackageId: string
  catalogDocId: number
  fileUrl: string | null
  fileName: string | null
  validUntil: string | null
  status: EstadoDocumento
  reviewComment: string | null
  reviewedByUserId: string | null
  reviewedAt: string | null
  uploadedAt: string | null
}

/** Una de las 14 casillas: el nombre del catálogo y su documento, si ya se subió. */
export interface CasillaDelPaquete {
  catalogDocId: number
  name: string
  document: DocumentoDelPaquete | null
}

export interface PaqueteMensual {
  exists: boolean
  id?: string
  month: number
  year: number
  /** null cuando el paquete del mes todavía no existe. */
  status: EstadoPaquete | null
  documents: CasillaDelPaquete[]
}

/**
 * Fila de la bandeja de Contraloría. El back devuelve el paquete completo con
 * su proveedor cargado, no un DTO recortado, así que se declaran solo los
 * campos que la pantalla usa.
 */
export interface PaquetePendienteDeRevision {
  id: string
  supplierId: string
  month: number
  year: number
  status: EstadoPaquete
  updatedAt: string
  supplier?: {
    id: string
    legalName?: string | null
    commercialName?: string | null
    rfc?: string | null
  }
}

export interface PaqueteDelHistorial {
  id: string
  month: number
  year: number
  status: EstadoPaquete
}

const comoProveedor = { token: 'proveedor' } as const
const comoStaff = { token: 'staff' } as const

const portal = '/suppliers/profile/monthly-package'
const interno = '/internal/suppliers/monthly-packages'

const conMesYAnio = (month?: number, year?: number) =>
  month && year ? `?month=${month}&year=${year}` : ''

export const paqueteMensualService = {
  // ── Portal del proveedor ──────────────────────────────────────────────────
  async miPaquete(month?: number, year?: number): Promise<PaqueteMensual> {
    return (await httpRequest<PaqueteMensual>(
      `${portal}${conMesYAnio(month, year)}`,
      comoProveedor,
    ))!
  },

  async miHistorial(): Promise<PaqueteDelHistorial[]> {
    return (await httpRequest<PaqueteDelHistorial[]>(`${portal}/history`, comoProveedor)) ?? []
  },

  /**
   * Sube el documento de una casilla. El back solo acepta si el paquete está
   * `pending` o `rejected`; una vez enviado a revisión queda cerrado.
   */
  async subirDocumento(
    catalogDocId: number,
    file: File,
    validUntil?: string,
  ): Promise<DocumentoDelPaquete> {
    const formData = new FormData()
    formData.append('file', file)
    if (validUntil) formData.append('validUntil', validUntil)

    return (await httpRequest<DocumentoDelPaquete>(`${portal}/documents/${catalogDocId}`, {
      ...comoProveedor,
      method: 'POST',
      body: formData,
    }))!
  },

  /** Pasa el paquete a revisión. El back valida que estén los 14. */
  async enviarARevision(): Promise<PaqueteMensual> {
    return (await httpRequest<PaqueteMensual>(`${portal}/submit`, {
      ...comoProveedor,
      method: 'POST',
    }))!
  },

  // ── Panel interno (Contraloría) ───────────────────────────────────────────
  async paqueteDeProveedor(
    supplierId: string,
    month?: number,
    year?: number,
  ): Promise<PaqueteMensual> {
    return (await httpRequest<PaqueteMensual>(
      `${interno}/${supplierId}${conMesYAnio(month, year)}`,
      comoStaff,
    ))!
  },

  /** Paquetes que ya envio el proveedor y esperan revision de Contraloria. */
  async pendientesDeRevision(): Promise<PaquetePendienteDeRevision[]> {
    return (
      (await httpRequest<PaquetePendienteDeRevision[]>(`${interno}/pending-review`, comoStaff)) ?? []
    )
  },

  /**
   * Aprueba o rechaza un documento. El back recalcula el estado del paquete
   * después de cada acción, así que hay que releer el paquete al terminar.
   */
  async revisarDocumento(
    documentId: string,
    status: 'approved' | 'rejected',
    reviewComment?: string,
    validUntil?: string,
  ): Promise<DocumentoDelPaquete> {
    return (await httpRequest<DocumentoDelPaquete>(`${interno}/documents/${documentId}`, {
      ...comoStaff,
      method: 'PATCH',
      body: JSON.stringify({
        status,
        ...(reviewComment ? { reviewComment } : {}),
        ...(validUntil ? { validUntil } : {}),
      }),
    }))!
  },
}
